import { MultipleOptionButton } from '@/global/components/MultipleOptionButton'
import { Tooltip } from '@/global/components/ui/tooltip'
import { useTransmit } from '@/hooks/useTransmit'
import {
    CalculatorIcon,
    CalendarRangeIcon,
    FileArchiveIcon,
    FileUserIcon,
    ShieldBanIcon,
} from 'lucide-react'
import React from 'react'
import { useLocation } from 'react-router-dom'
import { calculo, espelhoPonto } from 'service/api'
import { toast } from 'sonner'
import { useStore } from 'store'
import { useCalculoStore } from './store'
import { EventCalculoMessage, EventEspelhoPdfMessage } from './types'

type Props = {
    competencia: string
    funcionarioIds?: number[]
}

const allOptions = {
    CALCULAR: {
        all: {
            btnLabel: 'Calcular todos',
            menuLabel: 'Apenas calcular o ponto de todos',
        },
        selected: {
            btnLabel: 'Calcular selecionados',
            menuLabel: 'Calcular o ponto dos selecionados',
        },
        icon: <CalculatorIcon />,
        value: 'CALCULAR' as const,
    },
    PDF: {
        all: {
            btnLabel: 'Calcular e baixar PDF',
            menuLabel: 'Calcular todos e baixar relatório PDF',
        },
        selected: {
            btnLabel: 'Calcular selecionados e baixar PDF',
            menuLabel: 'Calcular selecionados e baixar relatório PDF',
        },
        icon: <FileUserIcon />,
        value: 'PDF' as const,
    },
    ZIP: {
        all: {
            btnLabel: 'Calcular e baixar ZIP',
            menuLabel: 'Calcular todos e baixar arquivo ZIP',
        },
        selected: {
            btnLabel: 'Calcular selecionados e baixar ZIP',
            menuLabel: 'Calcular selecionados e baixar arquivo ZIP',
        },
        icon: <FileArchiveIcon />,
        value: 'ZIP' as const,
    },
}

const optionKeys = ['CALCULAR', 'PDF', 'ZIP'] as const

const CalcularEmpresa: React.FC<Props> = ({
    competencia,
    funcionarioIds = [],
}) => {
    const hasSelection = funcionarioIds.length > 0
    const variant = hasSelection ? 'selected' : 'all'
    const options = optionKeys.map(key => ({
        ...allOptions[key][variant],
        icon: allOptions[key].icon,
        value: allOptions[key].value,
    }))
    const { progresso, state } = useCalculoStore(store => store.state)
    const dispatch = useCalculoStore(store => store.dispatch)

    const empresaId = useStore(state => state.companyId)
    const centroCustoId = useStore(state => state.costCenterId)
    const competenciaAberta = useStore(state => state.company?.competenciaAtual)

    const [selectedIndex, setSelectedIndex] = React.useState(0)
    const optionRef = React.useRef<(typeof optionKeys)[number]>('CALCULAR')
    /** Mantém os search params (seleção da tabela) ao abrir o modal. */
    const { search } = useLocation()

    const run = async (option: (typeof optionKeys)[number]) => {
        optionRef.current = option

        await mutateAsync({
            competencia,
            empresaId,
            centroCustoId: centroCustoId > 0 ? centroCustoId : undefined,
            baixarPdf: option === 'PDF',
            baixarZip: option === 'ZIP',
            ...(hasSelection ? { funcionarioIds } : {}),
        })
    }

    const subscribePdfProcessor = useTransmit<EventEspelhoPdfMessage>({
        onMessage: async ({ status, data }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    if (useCalculoStore.getState().state.progresso === null) {
                        toast.info(
                            'Estamos gerando os espelhos de ponto. Fique à vontade para utilizar o sistema normalmente.',
                            {
                                duration: 5 * 1000,
                            },
                        )
                    }

                    dispatch({ action: 'PROGRESSO', data: data.progresso })

                    break
                case 'ERRO':
                    toast.error(data.message)
                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    break
                case 'FINALIZADO':
                    if (data.pdf) {
                        await espelhoPonto.calculos.file(`zip/${data.pdf}`)
                    }
                    if (data.zip) {
                        await espelhoPonto.calculos.file(`zip/${data.zip}`)
                    }

                    await unsubscribe()
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    toast.success('Arquivos gerados com sucesso')
                    break
            }
        },
    })

    const subscribeCalculoProcessor = useTransmit<EventCalculoMessage>({
        onMessage: async ({ status, progresso, message }, unsubscribe) => {
            switch (status) {
                case 'PROCESSANDO':
                    dispatch({ action: 'PROGRESSO', data: progresso })
                    break
                case 'ERRO':
                    dispatch(
                        { action: 'PROGRESSO', data: null },
                        { action: 'PROCESSANDO', data: false },
                    )
                    await unsubscribe()
                    espelhoPonto.invalidateQueries()
                    toast.error(message ?? 'Ocorreu um erro no cálculo')
                    break
                default: {
                    dispatch({ action: 'PROGRESSO', data: null })
                    await unsubscribe()
                    espelhoPonto.invalidateQueries()
                    toast.success('O cálculo foi realizado com sucesso')

                    const option = optionRef.current

                    // Se não for para baixar PDF/ZIP, finaliza o processamento
                    if (option === 'CALCULAR') {
                        dispatch({ action: 'PROCESSANDO', data: false })
                        return
                    }

                    const channel = `processor:${
                        useCalculoStore.getState().state.ultimoProcesso
                    }`

                    await subscribePdfProcessor(channel)
                    break
                }
            }
        },
    })

    const { mutateAsync, isLoading } = calculo.historico.mutations.useCreate({
        onMutate: () =>
            dispatch(
                { action: 'PROGRESSO', data: 0 },
                { action: 'PROCESSANDO', data: true },
            ),
        onSuccess: async data => {
            dispatch({ action: 'ULTIMO_PROCESSO', data: data.id })

            const bloqueados = data.funcionariosComEspelhoAceito ?? []

            if (bloqueados.length > 0) {
                const LIMITE_NOMES = 5
                const nomes = bloqueados
                    .slice(0, LIMITE_NOMES)
                    .map(it => it.nome)
                    .join(', ')
                const resto =
                    bloqueados.length > LIMITE_NOMES
                        ? ` e mais ${bloqueados.length - LIMITE_NOMES}`
                        : ''

                toast.warning(
                    bloqueados.length === 1
                        ? `Um funcionário não foi calculado porque já aceitou o espelho: ${nomes}${resto}`
                        : `${bloqueados.length} funcionários não foram calculados porque já aceitaram o espelho: ${nomes}${resto}`,
                    { duration: 8 * 1000 },
                )
            }

            await subscribeCalculoProcessor(data.cod)
        },
        onError: async err => {
            dispatch(
                { action: 'PROGRESSO', data: null },
                { action: 'PROCESSANDO', data: false },
            )
            toast.error(err.message)
        },
    })

    /** Processamento em andamento bloqueia o botão inteiro, inclusive o menu. */
    const processando = React.useMemo(
        () => isLoading || progresso != null || state !== 'idle',
        [isLoading, progresso, state],
    )

    const fechada = competenciaAberta !== competencia
    /** Competência fechada só bloqueia cálculo — exportar período segue liberado. */
    const disableButton = processando || fechada
    const mainLabel = (() => {
        if (fechada) return 'Competência fechada'
        if (state === 'processando') return 'Processando...'
        if (isLoading || disableButton) return 'Calculando...'
        return options[0].btnLabel
    })()

    const buttonOptions = [
        ...options.map((option, index) => ({
            label: option.menuLabel,
            buttonLabel: index === 0 ? mainLabel : undefined,
            icon: index === 0 && fechada ? <ShieldBanIcon /> : option.icon,
            onClick: () => run(option.value),
            disabled: fechada,
        })),
        {
            label: 'Exportar período...',
            icon: <CalendarRangeIcon />,
            href: `/manutencao-ponto/calculo-ponto/exportar-periodo${search}`,
        },
    ]

    return (
        <div className="flex flex-1 items-center justify-end gap-3">
            <Tooltip title={fechada ? 'Competência fechada' : undefined}>
                <div>
                    <MultipleOptionButton
                        id="calcular-empresa"
                        options={buttonOptions}
                        disabled={processando}
                        progress={progresso}
                    />
                </div>
            </Tooltip>
        </div>
    )
}

export default CalcularEmpresa
